/**
 * Hook for managing bulk upload operations.
 *
 * Generates a batchId per drop event, uploads files via presigned URLs,
 * and tracks per-file progress. Fire-and-forget: no processing status polling.
 *
 * GH#1191 — two-phase ordering guarantee:
 *   Phase 1 (sequential): call initiateBulkUpload for each file IN SELECTION
 *     ORDER, passing its 0-based orderIndex. This mints catalog numbers on the
 *     backend in the user's intended order.
 *   Phase 2 (bounded-parallel): upload chunks and complete each session with
 *     MAX_CONCURRENT_UPLOADS concurrency so throughput is unchanged.
 */
import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { initiateBulkUpload, uploadChunk, completeBulkUpload } from '../utils/bulkUploadApi';
import { useActiveCatalog } from './useActiveCatalog';

const MAX_CONCURRENT_UPLOADS = 3;
const CHUNK_SIZE = 5 * 1024 * 1024; // 5 MB

export type BulkUploadStatus = 'pending' | 'uploading' | 'submitted' | 'failed';

export interface BulkUploadItem {
  id: string;
  file: File;
  status: BulkUploadStatus;
  progress: number;
  error?: string;
  batchId: string;
  catalogAliId: string;
}

export interface UseBulkUploadReturn {
  uploadFiles: (files: File[]) => void;
  uploadQueue: BulkUploadItem[];
  clearCompleted: () => void;
  isUploading: boolean;
}

interface InitiatedSession {
  id: string;
  file: File;
  batchId: string;
  catalogAliId: string;
  uploadId: string;
  key: string;
  presignedUrls: Array<{ partNumber: number; url: string }>;
}

export function useBulkUpload(): UseBulkUploadReturn {
  const { activeCatalogAliId } = useActiveCatalog();
  const [uploadQueue, setUploadQueue] = useState<BulkUploadItem[]>([]);
  const queueRef = useRef<BulkUploadItem[]>([]);

  // The two-phase pipeline is fire-and-forget, so its async callbacks can
  // resolve after the component unmounts. Gate state writes on this ref so a
  // late initiate/upload callback can't setState on an unmounted hook. Set in
  // an effect (not at declaration) so StrictMode's mount→unmount→remount
  // re-arms it correctly.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const updateItem = useCallback((id: string, updates: Partial<BulkUploadItem>) => {
    if (!isMountedRef.current) return;
    setUploadQueue((prev) => {
      const next = prev.map((item) => (item.id === id ? { ...item, ...updates } : item));
      queueRef.current = next;
      return next;
    });
  }, []);

  /**
   * Phase 2: upload chunks + complete for a single session.
   * Runs concurrently with other sessions (bounded externally via semaphore).
   */
  const uploadSession = useCallback(
    async (session: InitiatedSession): Promise<void> => {
      updateItem(session.id, { status: 'uploading' });
      try {
        const totalChunks = Math.ceil(session.file.size / CHUNK_SIZE);
        const parts: Array<{ ETag: string; PartNumber: number }> = [];

        for (let i = 0; i < totalChunks; i++) {
          const start = i * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, session.file.size);
          const chunk = session.file.slice(start, end);

          const etag = await uploadChunk(
            chunk,
            session.presignedUrls[i].url,
            (loaded, total) => {
              const chunkProgress = (i + loaded / total) / totalChunks;
              updateItem(session.id, { progress: Math.round(chunkProgress * 100) });
            },
          );

          parts.push({ ETag: etag, PartNumber: i + 1 });
        }

        await completeBulkUpload(session.uploadId, session.key, parts);
        updateItem(session.id, { status: 'submitted', progress: 100 });
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Upload failed';
        updateItem(session.id, { status: 'failed', error: errorMessage });
      }
    },
    [updateItem],
  );

  const uploadFiles = useCallback(
    (files: File[]) => {
      // Defensive short-circuit: the UI should already block file
      // selection when no catalog is active, but guard the wire-call
      // path here too so a stale uploadFiles ref can never silently
      // dispatch uncatalog'd uploads.
      if (!activeCatalogAliId) return;

      const batchId = uuidv4();
      // Capture the catalog ALI ID at queue time so a mid-batch
      // catalog switch can't redirect already-queued items.
      const catalogAliId = activeCatalogAliId;

      const newItems: BulkUploadItem[] = files.map((file) => ({
        id: uuidv4(),
        file,
        status: 'pending' as const,
        progress: 0,
        batchId,
        catalogAliId,
      }));

      setUploadQueue((prev) => {
        const next = [...prev, ...newItems];
        queueRef.current = next;
        return next;
      });

      // Kick off the two-phase upload asynchronously so we don't block
      // the React render cycle.
      void (async () => {
        // --- Phase 1: sequential initiation in selection order ---
        // GH#1191: mint catalog numbers in drop order by awaiting each
        // initiateBulkUpload call before issuing the next. The 0-based
        // orderIndex is forwarded to the backend so it can stamp the
        // pre-assigned number at the right sequence position.
        //
        // Trade-off: an abandoned or failed single-file upload burns a
        // catalog number, leaving a gap. This is intentional: ordering
        // requires committing the number before completion order is known.
        const sessions: InitiatedSession[] = [];
        for (let i = 0; i < newItems.length; i++) {
          const item = newItems[i];
          try {
            const session = await initiateBulkUpload(item.file, item.catalogAliId, item.batchId, i);
            sessions.push({
              id: item.id,
              file: item.file,
              batchId: item.batchId,
              catalogAliId: item.catalogAliId,
              uploadId: session.uploadId,
              key: session.key,
              presignedUrls: session.presignedUrls,
            });
          } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Initiation failed';
            updateItem(item.id, { status: 'failed', error: errorMessage });
          }
        }

        // --- Phase 2: bounded-parallel chunk upload + completion ---
        // Run up to MAX_CONCURRENT_UPLOADS uploads concurrently to
        // preserve throughput while keeping the phase-1 ordering intact.
        const pending = [...sessions];
        let active = 0;

        await new Promise<void>((resolve) => {
          if (pending.length === 0) {
            resolve();
            return;
          }

          const runNext = () => {
            while (active < MAX_CONCURRENT_UPLOADS && pending.length > 0) {
              const session = pending.shift()!;
              active += 1;
              uploadSession(session).finally(() => {
                active -= 1;
                if (pending.length > 0) {
                  runNext();
                } else if (active === 0) {
                  resolve();
                }
              });
            }
            if (pending.length === 0 && active === 0) {
              resolve();
            }
          };

          runNext();
        });
      })();
    },
    [activeCatalogAliId, updateItem, uploadSession],
  );

  const clearCompleted = useCallback(() => {
    setUploadQueue((prev) => {
      const next = prev.filter((item) => item.status !== 'submitted');
      queueRef.current = next;
      return next;
    });
  }, []);

  const isUploading = useMemo(
    () => uploadQueue.some((item) => item.status === 'uploading' || item.status === 'pending'),
    [uploadQueue],
  );

  return { uploadFiles, uploadQueue, clearCompleted, isUploading };
}
